import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Bell, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useUiStore } from "@/stores/ui";
import { PANEL_PATH } from "@/admin/types";
import {
  getNotificationPermission,
  isNotificationPromptSuppressed,
  isNotificationSupported,
} from "@/lib/notifications";

const SHOW_DELAY_MS = 2500;

export function NotificationPrompt() {
  const location = useLocation();
  const {
    cookieConsent,
    notificationsEnabled,
    notificationPromptDismissed,
    enableNotifications,
    dismissNotificationPrompt,
    syncNotificationsFromBrowser,
  } = useUiStore();
  const onFormPage =
    location.pathname.startsWith("/anuncie") ||
    location.pathname.startsWith("/contato") ||
    location.pathname.startsWith("/entrar") ||
    location.pathname.startsWith(PANEL_PATH);
  const [ready, setReady] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission | "unsupported">(
    () => getNotificationPermission(),
  );
  const [activating, setActivating] = useState(false);
  // Evita flash do prompt antes do persist hidratar / se só o localStorage tiver o dismiss
  const [localDismissed, setLocalDismissed] = useState(() => isNotificationPromptSuppressed());

  useEffect(() => {
    const current = syncNotificationsFromBrowser();
    setPermission(current);

    // Reage se o usuário mudar a permissão nas configurações do browser
    let status: PermissionStatus | null = null;
    let cancelled = false;

    async function watchPermission() {
      if (!isNotificationSupported() || !navigator.permissions?.query) return;
      try {
        status = await navigator.permissions.query({
          name: "notifications" as PermissionName,
        });
        if (cancelled) return;
        status.onchange = () => {
          const next = syncNotificationsFromBrowser();
          setPermission(next);
        };
      } catch {
        // Safari / browsers sem Permissions API para notifications
      }
    }

    void watchPermission();
    return () => {
      cancelled = true;
      if (status) status.onchange = null;
    };
  }, [syncNotificationsFromBrowser]);

  useEffect(() => {
    if (cookieConsent === "pending") {
      setReady(false);
      return;
    }
    const timer = window.setTimeout(() => setReady(true), SHOW_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [cookieConsent]);

  // localStorage é a fonte de verdade; Zustand/local state só reforçam o hide imediato
  const suppressed =
    localDismissed || notificationPromptDismissed || isNotificationPromptSuppressed();

  const canRequest = permission === "default";
  const isBlocked = permission === "denied";

  const visible =
    ready &&
    cookieConsent !== "pending" &&
    !onFormPage &&
    !notificationsEnabled &&
    !suppressed &&
    isNotificationSupported() &&
    (canRequest || isBlocked);

  function handleDismiss() {
    setLocalDismissed(true);
    dismissNotificationPrompt();
  }

  async function handleActivate() {
    if (isBlocked) {
      // Browser bloqueou o site — só o usuário pode reativar nas configurações
      return;
    }
    setActivating(true);
    try {
      const granted = await enableNotifications();
      const next = getNotificationPermission();
      setPermission(next);
      if (!granted) {
        // Recusou no diálogo nativo — não voltar a pedir a cada refresh
        setLocalDismissed(true);
      }
    } finally {
      setActivating(false);
    }
  }

  return (
    <>
      {visible ? (
        <aside
          className="animate-rise-in fixed bottom-6 left-4 right-4 z-40 mx-auto max-w-md rounded-lg border border-brand-primary/30 bg-brand-surface-lowest p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-float sm:left-auto sm:right-6"
          aria-live="polite"
          role="dialog"
          aria-labelledby="notification-prompt-title"
        >
          <div className="flex items-start gap-3">
            <div className="rounded bg-brand-chrome p-2 text-white">
              <Bell className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p
                id="notification-prompt-title"
                className="font-display text-sm font-bold text-brand-ink"
              >
                {isBlocked
                  ? "Notificações bloqueadas neste site"
                  : "Quer ser avisado de novas notícias?"}
              </p>
              <p className="mt-1 text-sm text-brand-on-surface-variant">
                {isBlocked
                  ? "O navegador bloqueou os alertas. Clique no ícone de cadeado/informações na barra de endereço, permita notificações e recarregue a página."
                  : "Ative as notificações e o Eusébio News avisa no seu celular ou computador quando publicarmos matérias novas — mesmo com o site fechado."}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {isBlocked ? (
                  <Button size="sm" className="rounded" onClick={handleDismiss}>
                    Entendi
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    className="rounded"
                    disabled={activating}
                    onClick={() => void handleActivate()}
                  >
                    {activating ? "Ativando…" : "Ativar alertas"}
                  </Button>
                )}
                {!isBlocked ? (
                  <Button size="sm" variant="ghost" className="rounded" onClick={handleDismiss}>
                    Agora não
                  </Button>
                ) : null}
              </div>
            </div>
            <button
              type="button"
              onClick={handleDismiss}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-brand-outline hover:bg-brand-surface-container"
              aria-label="Fechar"
              style={{ touchAction: "manipulation" }}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </aside>
      ) : null}
    </>
  );
}
